/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import * as stores from "stores";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { bookmarksStatuses, lhpFeatures, AJSMetadataStatus } from "constants/enums";
import { isBoolean, isArray } from "utils/lodashUtils";
import { currLHPFeature } from "selectors/lhpSelectors";
import { getAJSMetadataStatus } from "selectors/documentSelectors";
import { getHasBookmarksFromMetadata } from "selectors/bookmarksSelectors";
import { shouldShowBookmarksIcon } from "selectors/bookmarksSelectors";

export class BookmarkStore {
  @observable bookmarksStatus;
  @observable shouldEnableBookmarks;
  bookmarkIdToActions = new Map();
  bookmarks = [];
  _getBookmarkPromise = null;
  initializeOpts = {};
  shouldShowExpandedComponent = false;

  constructor() {
    this.init();
    this.postBookmarksStatusLookupHandler = this.postBookmarksStatusLookupHandler.bind(this);
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    subscribeToSelector(stores, isInitialPageLoaded, this.getBookmarksStatusFromDocStore.bind(this));
    this.bookmarksStatus = bookmarksStatuses.HAS_CONTENT_CALL_TO_BE_MADE;
    this.bookmarks = [];
    this.shouldEnableBookmarks = true;
    this._getBookmarkPromise = null;
  }

  @action
  setShouldEnableBookmarks(value) {
    if (isBoolean(value)) {
      this.shouldEnableBookmarks = value;
    }
  }

  @action
  setBookmarksStatus(value) {
    this.bookmarksStatus = value;
  }

  @action
  getBookmarksStatusFromDocStore() {
    if (getAJSMetadataStatus(stores) === AJSMetadataStatus.SUCCEEDED && isInitialPageLoaded(stores) === true) {
      if (getHasBookmarksFromMetadata(stores)) {
        this.setBookmarksStatus(bookmarksStatuses.PRESENT_AND_NOT_FETCHED);
      } else {
        this.setBookmarksStatus(bookmarksStatuses.NOT_PRESENT);
      }
      this.postBookmarksStatusLookupHandler();
    }
  }

  @action
  postBookmarksStatusLookupHandler() {
    if (currLHPFeature(stores) === lhpFeatures.BOOKMARKS) {
      if (this.bookmarksStatus === bookmarksStatuses.PRESENT_AND_NOT_FETCHED) {
        this.getBookmarksFromDocAPI();
      } else if (this.bookmarksStatus === bookmarksStatuses.NOT_PRESENT) {
        stores.lhpStore.setCurrLHPFeature(lhpFeatures.NONE);
      }
    }
  }

  @action
  setBookmarkIdToActionsMap(value) {
    if (value) {
      for (let i = 0; i < value.length; i++) {
        const actions = value[i].actions;
        this.bookmarkIdToActions.set(value[i].id, actions);
        this.setBookmarkIdToActionsMap(value[i].children);
        delete value[i].actions;
      }
    }
  }

  @action
  setBookmarks(value) {
    if (isArray(value) && value.length > 0) {
      this.setBookmarkIdToActionsMap(value);
      this.bookmarks = value;
    }
  }

  @action
  getBookmarksFromDocAPI(forceFetch = false) {
    if (!this._getBookmarkPromise || forceFetch) {
      this._getBookmarkPromise = new Promise((resolve, reject) => {
        if (this.bookmarksStatus === bookmarksStatuses.PRESENT_AND_NOT_FETCHED || forceFetch) {
          this.setBookmarksStatus(bookmarksStatuses.PRESENT_AND_FETCHING);
          this.docApi
            .loadBookmarks()
            .then(resp => {
              const bookmarks = resp.data.bookmarks;
              if (isArray(bookmarks) && bookmarks.length > 0) {
                this.setBookmarks(bookmarks);
                this.setBookmarksStatus(bookmarksStatuses.PRESENT_AND_FETCHED);
                resolve(this.bookmarks);
              } else {
                this.setBookmarksStatus(bookmarksStatuses.NOT_PRESENT);
                reject();
              }
            })
            .catch(() => {
              this.setBookmarksStatus(bookmarksStatuses.NOT_PRESENT);
              reject();
            });
        } else if (
          this.bookmarksStatus === bookmarksStatuses.PRESENT_AND_FETCHED ||
          this.bookmarksStatus === bookmarksStatuses.NOT_PRESENT
        ) {
          resolve(this.bookmarks);
        } else {
          reject();
        }
      });
    }
    return this._getBookmarkPromise;
  }

  setInitializeOpts(opts) {
    this.initializeOpts = opts;
  }

  initializeAction() {
    const updateShouldShowBookmarksDropin = shouldShowBookmarks => {
      this.initializeOpts.showCallback && this.initializeOpts.showCallback(shouldShowBookmarks);
      this.shouldShowExpandedComponent = shouldShowBookmarks;
    };

    subscribeToSelector(this.stores, shouldShowBookmarksIcon, showIcon => {
      if (showIcon === true) {
        updateShouldShowBookmarksDropin(true);
        this.getBookmarksFromDocAPI().catch(() => {
          updateShouldShowBookmarksDropin(false);
        });
      } else {
        updateShouldShowBookmarksDropin(false);
      }
    });
  }
}
